import { useState, useRef, useCallback } from 'react';

const useAudio = () => {
  const [stream, setStream] = useState(null);
  const [isMicAvailable, setIsMicAvailable] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);

  const requestMicrophone = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
        },
      });
      setStream(mediaStream);
      setIsMicAvailable(true);
      setupAudioAnalyser(mediaStream);
      return mediaStream;
    } catch (error) {
      setIsMicAvailable(false);
      throw error;
    }
  }, []);

  const setupAudioAnalyser = (mediaStream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(mediaStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(avg / 255);
          rafRef.current = requestAnimationFrame(updateLevel);
        }
      };
      rafRef.current = requestAnimationFrame(updateLevel);
    } catch (error) {
      console.error('Audio analyser setup error:', error);
    }
  };

  const startRecording = useCallback(() => {
    if (!stream) return;
    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
    });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start(100);
    mediaRecorderRef.current = recorder;
  }, [stream]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        resolve(blob);
      };
      mediaRecorderRef.current.stop();
    });
  }, []);

  const stopStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    sourceRef.current = null;
    setAudioLevel(0);
    setIsMicAvailable(false);
  }, [stream]);

  return {
    stream,
    isMicAvailable,
    audioLevel,
    requestMicrophone,
    startRecording,
    stopRecording,
    stopStream,
  };
};

export default useAudio;
